import { rufe } from './client';

/**
 * Der Arbeitsplatz (SYS-12, Pflichtenheft M-02).
 *
 * Die Typen spiegeln `apps/api/src/modul/auswertung/domaene/uebersicht.typen.ts`.
 * Sie stehen hier ein zweites Mal, wie bei jeder anderen Maske auch: Der
 * Vertrag `@crm/kontrakte` beschreibt die ÖFFENTLICHE Buchungsschnittstelle
 * (Kap. 11) und nicht die Innenseite zwischen unserer API und unserer
 * Oberfläche.
 */

/** Die drei Stufen aus Abschnitt 2.10. Mehr Farben hat die Oberfläche nicht. */
export type Ampel = 'ruhig' | 'achtung' | 'kritisch';

/*
 * AUS DEM KONTRAKT (B8, 12.09.2026).
 *
 * Nur der Typ — `import type` verschwindet beim Uebersetzen restlos. Eine
 * Werteinfuhr aus @crm/kontrakte zoege zod in das Buendel, weil das Paket
 * CommonJS ist; der Grund steht ausfuehrlich im Kontrakt.
 */
import type { Bereichskennung, Kachelgruppe } from '@crm/kontrakte';

export type { Bereichskennung, Kachelgruppe };

export type Kachel = {
  readonly kennung: string;
  readonly gruppe: Kachelgruppe;
  readonly beschriftung: string;
  /** Als Text: ein Geldbetrag verträgt kein binäres Gleitkomma. */
  readonly wert: string;
  readonly einheit: 'anzahl' | 'euro';
  readonly zusatz: string | null;
  readonly ampel: Ampel;
  /** Auftrag #37: die Veränderung gegen den Vorzeitraum; null bei einem Bestand ohne Geschichte. */
  readonly veraenderung: Veraenderung | null;
  /** AUS-11: die Ampel gegen den Zielwert, wo es einen gibt. */
  readonly ziel: { readonly text: string; readonly ampel: Ampel } | null;
  /** B8: die Kennung aus dem Kontrakt, kein freies Wort. */
  readonly bereich: Bereichskennung | null;
};

/** Auftrag #37: „mehr oder weniger ist besser" kommt aus dem Kennzahlenkatalog der API. */
export type Veraenderung = {
  readonly vorher: string;
  /** Prozent mit einer Stelle, Punkt als Trenner; null, wenn der Vorzeitraum 0 war. */
  readonly prozent: string | null;
  readonly gegen: string;
  readonly besser: 'mehr' | 'weniger';
};

/* ── Verlauf und Liste je Widget (Auftrag #37), nachgeladen ─────────────── */

export type Verlaufspunkt = {
  readonly periode: string;
  readonly bezeichnung: string;
  readonly wert: string;
  readonly laufend: boolean;
};

export type Widgetverlauf = {
  readonly titel: string;
  readonly kennzahl: string;
  readonly einheit: 'anzahl' | 'euro';
  readonly periode: 'woche' | 'monat';
  readonly punkte: readonly Verlaufspunkt[];
};

/** Wohin ein Listeneintrag springt — die Akte des Datensatzes. */
export type Akteziel =
  | { readonly art: 'kunde' | 'angebot' | 'auftrag' | 'rechnung'; readonly id: string }
  | { readonly art: 'buchung'; readonly nummer: string };

export type Listeneintrag = {
  readonly schluessel: string;
  readonly text: string;
  readonly zusatz: string | null;
  readonly wert: string | null;
  readonly einheit: 'anzahl' | 'euro';
  readonly ampel: Ampel;
  readonly ziel: Akteziel | null;
};

export type Widgetliste = {
  readonly titel: string;
  readonly eintraege: readonly Listeneintrag[];
  /** Ein Satz, was fehlt. */
  readonly leer: string;
};

export type Widgetdetail = {
  readonly kennung: string;
  readonly stand: string;
  readonly verlauf: Widgetverlauf | null;
  readonly liste: Widgetliste | null;
};

export type Aufgabe = {
  readonly kennung: string;
  readonly text: string;
  readonly anzahl: number;
  readonly dringlichkeit: Ampel;
  /** B8: die Kennung aus dem Kontrakt, kein freies Wort. */
  readonly bereich: Bereichskennung | null;
};

export type Uebersicht = {
  readonly stand: string;
  readonly kacheln: readonly Kachel[];
  readonly aufgaben: readonly Aufgabe[];
};

/** Die Überschrift je Gruppe — dieselben Wörter wie in der Menüzeile. */
export const GRUPPENNAME: Readonly<Record<Kachelgruppe, string>> = {
  betrieb: 'Betrieb',
  service: 'Service',
  vertrieb: 'Vertrieb',
  faktura: 'Faktura',
  system: 'System',
};

/**
 * Die Zustandsklasse je Ampelstufe.
 *
 * `ruhig` bekommt KEINE Klasse und keine Farbe. Eine Kachel, bei der alles in
 * Ordnung ist, soll nicht grün leuchten — sonst ist auf der Maske alles
 * bunt, und die eine Kachel, die wirklich etwas will, geht darin unter
 * (2.10: Farbe bedeutet etwas oder sie ist Stimmung).
 */
export const AMPELKLASSE: Readonly<Record<Ampel, string>> = {
  ruhig: '',
  achtung: 'kachel--warn',
  kritisch: 'kachel--krit',
};

/** Geldbeträge kommen als Dezimaltext mit Punkt und werden hier deutsch gesetzt. */
const EURO = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const ANZAHL = new Intl.NumberFormat('de-DE');

export function alsAnzeige(kachel: Kachel): string {
  if (kachel.einheit === 'euro') {
    const zahl = Number(kachel.wert);
    return Number.isFinite(zahl) ? EURO.format(zahl) : kachel.wert;
  }
  const zahl = Number(kachel.wert);
  return Number.isFinite(zahl) ? ANZAHL.format(zahl) : kachel.wert;
}

/**
 * NFA-01 (CRM-165): `nur` misst eine einzelne Kachel oder `aufgaben` — das
 * Dashboard lädt so jede Kachel für sich, und eine langsame hält die anderen
 * nicht auf. Ohne `nur` alles, was auf dem Dashboard liegt.
 */
export async function uebersicht(
  optionen: { readonly nur?: string; readonly signal?: AbortSignal } = {},
): Promise<Uebersicht> {
  const pfad = optionen.nur === undefined ? '/uebersicht' : `/uebersicht?nur=${encodeURIComponent(optionen.nur)}`;
  return rufe<Uebersicht>(pfad, optionen.signal === undefined ? {} : { signal: optionen.signal });
}

/** Ein Eintrag der Glocke (Auftrag CRM-170): etwas, das dem Benutzer selbst gehört. */
export type Glockeneintrag =
  | {
      readonly art: 'wiedervorlage';
      readonly id: string;
      readonly betreff: string;
      readonly kunde: { readonly id: string; readonly name: string } | null;
      readonly faelligAm: string;
      readonly ueberfaellig: boolean;
    }
  | {
      readonly art: 'option';
      readonly id: string;
      readonly termin: string;
      readonly produkt: string;
      readonly beginn: string;
      readonly plaetze: number;
      readonly fristBis: string;
    }
  | {
      readonly art: 'angebot';
      readonly id: string;
      readonly nummer: string;
      readonly betreff: string;
      readonly kunde: { readonly id: string; readonly name: string };
      readonly gueltigBis: string;
      readonly ueberfaellig: boolean;
    }
  /*
   * SUP-25, SUP-26 (Auftrag CRM-178): ein offener Supportvorgang, der dem
   * Benutzer gehört oder den er vertritt, oder einer, der an seine Rolle
   * eskaliert ist.
   */
  | {
      readonly art: 'supportvorgang';
      readonly id: string;
      readonly nummer: string;
      readonly betreff: string;
      /** Kennung des Postfachs — damit der Posteingang den richtigen Reiter öffnet. */
      readonly postfach: string;
      readonly faelligAm: string | null;
      readonly ueberfaellig: boolean;
      readonly eskaliert: boolean;
      /** SUP-26: Name dessen, den man vertritt; sonst null. */
      readonly vertretungFuer: string | null;
    };

export type Glocke = {
  readonly stand: string;
  readonly eintraege: readonly Glockeneintrag[];
  readonly faellig: number;
  readonly ueberfaellig: number;
};

/**
 * Die Glocke der Kopfzeile (Auftrag CRM-170): nur die eigenen fälligen
 * Wiedervorlagen, Optionen und Angebote — ein eigener, schmaler Endpunkt statt der
 * Übersicht, denn sie fragt alle fünf Minuten.
 *
 * `hintergrund` für die Abrufe im Takt: Sie halten die Sitzung nicht wach (BEN-07).
 */
export async function glocke(hintergrund = false): Promise<Glocke> {
  return rufe<Glocke>('/uebersicht/glocke', { hintergrund });
}

/**
 * Verlauf und Liste EINES Widgets (Auftrag #37) — nur, was die Karte in ihrer
 * Größe zeigt: ab 2×1 den Verlauf, ab 2×2 auch die Liste. Die Rechte prüft
 * die API wie für die Kachel selbst (SYS-12).
 */
export async function widgetdetail(
  kennung: string,
  teile: { readonly verlauf: boolean; readonly liste: boolean },
  signal?: AbortSignal,
): Promise<Widgetdetail> {
  const wahl = [teile.verlauf ? 'verlauf' : null, teile.liste ? 'liste' : null].filter((t) => t !== null).join(',');
  return rufe<Widgetdetail>(
    `/uebersicht/widget/${encodeURIComponent(kennung)}?teile=${wahl}`,
    signal === undefined ? {} : { signal },
  );
}

/** Ein Wert der Übersicht deutsch gesetzt — Euro ohne Cent, Anzahlen mit Punkt. */
export function zahlText(wert: string, einheit: 'anzahl' | 'euro'): string {
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return wert;
  return einheit === 'euro' ? EURO.format(zahl) : ANZAHL.format(zahl);
}

/*
 * ── Die eigene Anordnung (SYS-12 i. d. F. AeA-40, Entwurf) ─────────────────
 *
 * Welche Widgets zur Verfügung stehen, sagt die API aus den Rechten. Die
 * Maske ordnet nur, was darin steht — sie kennt die Kennungen nicht als
 * Aufzählung, damit ein neues Widget ohne Änderung hier erscheint.
 */

/*
 * Die Lage im freien Raster (Auftrag #35). Nur Typen — die Rechnung dazu
 * führt die Maske aus `@crm/kontrakte/src/raster` ein, derselben Datei, mit
 * der die API prüft.
 */
import type { Groesse, Rasterfeld } from '@crm/kontrakte';

export type { Groesse };

/** Ein Feld des Dashboards: welches Widget, wo, wie groß. */
export type Feld = Rasterfeld;

export type Widget = {
  readonly kennung: string;
  readonly beschriftung: string;
  /** Erscheint nur, wenn es etwas zu zeigen gibt. */
  readonly nurBeiBedarf: boolean;
  /** Die Größe, mit der es beim Einfügen ins Raster kommt — die API setzt sie je Art. */
  readonly vorgabe: Groesse;
};

export type Anordnung = {
  /** Die Felder mit ihrer Lage, in Lesefolge (Auftrag #35). */
  readonly felder: readonly Feld[];
  /** false: Es gilt die Vorgabe aus den Rechten. */
  readonly eigene: boolean;
  readonly verfuegbar: readonly Widget[];
  /** Was ohne Zutun entfallen ist — ein gelöschter oder nicht mehr geteilter Bericht (Auftrag #27). */
  readonly hinweise: readonly string[];
};

/** „Das steht an" — seit Auftrag #35 ein Widget (SYS-12). */
export const AUFGABEN = 'aufgaben';

/** Das Widget „Zuletzt geöffnet" (M-07). */
export const VERLAUF = 'verlauf';

/** Ein Bericht als Widget: `bericht:<id>` (Auftrag #27). */
export const BERICHTSWIDGET = 'bericht:';

export async function anordnung(): Promise<Anordnung> {
  return rufe<Anordnung>('/uebersicht/anordnung');
}

/** Die ganze Belegung des Rasters: Was fehlt, ist entfernt. Serverseitig je Benutzer abgelegt. */
export async function anordnen(felder: readonly Feld[]): Promise<Anordnung> {
  return rufe<Anordnung>('/uebersicht/anordnung', { verfahren: 'PUT', rumpf: { felder } });
}

/** „Auf Vorgabe zurücksetzen". */
export async function aufVorgabe(): Promise<Anordnung> {
  return rufe<Anordnung>('/uebersicht/anordnung', { verfahren: 'DELETE' });
}
